<script setup>
import GenerateKeywordManagementAction from '@frontJs/components/actions/projectManagement/sea/GenerateKeywordManagementAction.vue';
import ArrowsClockwiseIcon from '@iconify-vue/ph/arrows-clockwise';
import CopySimpleIcon from '@iconify-vue/ph/copy-simple';
import DownloadSimpleIcon from '@iconify-vue/ph/download-simple';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import DisplayLineChartAction from '@js/components/actions/DisplayLineChartAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { toastSuccess } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID',
                    width: 0,
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'cpcFeedUrl',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Flux<br/>CPC',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                href: props.row.original.cpcFeedUrl,
                                target: '_blank',
                                type: 'secondary',
                                size: 'small',
                                square: true,
                                icon: DownloadSimpleIcon,
                            }),
                            h(ActionButton, {
                                callback: () => {
                                    navigator.clipboard.writeText(
                                        props.row.original.cpcFeedUrl,
                                    );

                                    toastSuccess('Copié avec succès.');
                                },
                                target: '_blank',
                                type: 'secondary',
                                size: 'small',
                                square: true,
                                icon: CopySimpleIcon,
                            }),
                        ],
                    },
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'keywords',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Mots-clés',
                }),
            cell: (props) =>
                props.row.original.numberOfItems !== null
                    ? h(
                          NumberCell,
                          {
                              number: props.row.original.numberOfItems,
                          },
                          props.row.original.numberOfItemsChartData
                              ? {
                                    actions: () =>
                                        h(DisplayLineChartAction, {
                                            labels: props.row.original
                                                .numberOfItemsChartData.labels,
                                            datasets: [
                                                {
                                                    group: 'Mots-clés',
                                                    label: 'Mots-clés',
                                                    backgroundColor: '#4d65b9',
                                                    borderColor: '#4d65b9',
                                                    pointRadius: 0,
                                                    pointHoverRadius: 0,
                                                    fill: {
                                                        target: 'origin',
                                                        above: '#e4e8f4',
                                                    },
                                                    tension: 0.4,
                                                    data: props.row.original
                                                        .numberOfItemsChartData
                                                        .data,
                                                    labels: props.row.original
                                                        .numberOfItemsChartData
                                                        .labels,
                                                    yAxisID: 'y',
                                                },
                                            ],
                                            title: 'Mots-clés',
                                            withDecimalsAxis1: false,
                                            axis1BeginAtZero: true,
                                        }),
                                }
                              : {},
                      )
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'campaign',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Campagne',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.campaign,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'generatedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Généré le',
                }),
            cell: (props) =>
                props.row.original.generatedAt
                    ? h(TextCell, {
                          text: props.row.original.generatedAt,
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.sea.keywordManagement',
                                    {
                                        tenant: tenant.id,
                                        googleAdsKeywordManagement:
                                            props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                                square: true,
                            }),
                            h(
                                GenerateKeywordManagementAction,
                                {
                                    tenant: tenant,
                                    googleAdsKeywordManagement:
                                        props.row.original,
                                    callback: () => updateData(),
                                },
                                {
                                    button: ({ action, loading }) =>
                                        h(ActionButton, {
                                            size: 'small',
                                            title: 'Générer le flux CPC',
                                            callback: () => action(),
                                            loading,
                                            square: true,
                                            icon: ArrowsClockwiseIcon,
                                        }),
                                },
                            ),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.sea.postKeywordManagements', {
        tenant: tenant.id,
    }),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
        force-scroll
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="
                    route('front.projectManagement.sea.keywordManagement', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
